import Cookies from "js-cookie";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import ArrowIcon from "@/nav-localized/assets/header/arrow.svg?react";
import { useStoreState } from "../store/useStore";
import {
  dropDownStylesText,
  stylesText,
  popperTriggerHover,
  popperItemHover,
  popperDivider
} from "../styles";
import AssetsCard from "./AssetCard";
import styles from "./index.module.scss";
import { stylesBg } from "./styles";
import PopOver from "./../PopOver";
import UnlockableRewardCard from "./UnlockableRewardCard";
// 配置
import { showUnlockableRewardCard } from "@/nav-localized/config";

import useAction, {
  AccountMode,
  GetAccountModeResponse,
  NewUserTaskExpiredInfo
} from "../AccountMode/useAction";
import useAssets from "@/nav-localized/hooks/useAssets";
import clsx from "clsx";

const AssetsPopper = () => {
  const { theme, localeLink, isDemoTrading, userInfo, userAccountType, copyTradeUserInfo } =
    useStoreState();
  const copyTradeRole = Cookies.get("role") || copyTradeUserInfo?.role;
  const isTrader = copyTradeRole === "1";
  const assets = useAssets(isTrader);

  const { getAccountMode, getNewUserTaskExpiredInfo } = useAction();

  const t = useTranslations("header");

  const [currentAccountMode, setCurrentAccountMode] = useState<AccountMode>("ACCOUNT_LEVEL_NORMAL");
  const [newUserTaskInfo, setNewUserTaskInfo] = useState<NewUserTaskExpiredInfo | null>(null);
  const [showUnlockableReward, setShowUnlockableReward] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const initAccountMode = async () => {
    try {
      const res = await getAccountMode();
      const { code, msg, data } = res as unknown as {
        code: number;
        msg: string;
        data: GetAccountModeResponse;
      };
      if (code !== 200) {
        throw new Error(msg || "Get Account Mode Error");
      }
      setCurrentAccountMode(data.account_level);
    } catch (error) {
      console.info("Get Account Mode Error", error);
    }
  };

  const initNewUserTaskInfo = async () => {
    try {
      // 邀请码
      let referralCode = "";
      const q = window.next?.router?.query?.referral_code;
      if (q != null) {
        referralCode = typeof q === "string" ? q : Array.isArray(q) ? (q[0] ?? "") : "";
      } else if (typeof window !== "undefined") {
        referralCode = new URLSearchParams(window.location.search).get("referral_code") ?? "";
      }

      const res = await getNewUserTaskExpiredInfo<{
        code: number;
        msg?: string;
        data: NewUserTaskExpiredInfo;
      }>({ referral_code: referralCode });

      if (res.code !== 200) return;

      const data = res.data;
      // TODO:测试代码，记得删除
      // const data = {
      //   ...res.data,
      //   accepted_token_splash_new_user: false,
      //   new_user_task_expired: false,
      //   transfer_task_finish_status: true,
      //   trade_task_finish_status: true
      // };

      // 如果已报名TokenSplash的新用户任务或RewardsHub新人盲盒任务已过期，不展示 UnlockableRewardCard 组件
      // 判断条件： accepted_token_splash_new_user = true 或 new_user_task_expired = true
      if (data.accepted_token_splash_new_user || data.new_user_task_expired) {
        setShowUnlockableReward(false);
        return;
      }

      // 展示 UnlockableRewardCard
      setShowUnlockableReward(true);
      setNewUserTaskInfo(data);
    } catch (error) {
      console.error("Failed to fetch new user task info:", error);
      setShowUnlockableReward(false);
    }
  };

  const menus = useMemo(() => {
    let nav = assets.map((item) => ({ ...item }));

    if (isDemoTrading) {
      nav = nav.filter((item) => item.isSupportDemoTrading);
    }

    if (currentAccountMode === "ACCOUNT_LEVEL_NORMAL") {
      nav = nav.filter((item) => item.label !== "trading");
    } else {
      nav = nav.filter((item) => item.label !== "spotAccount" && item.label !== "futuresAccount");
    }

    const cookieAccountType = Cookies.get("Global-AccountType");
    if (
      cookieAccountType === "NORMAL_SUB_ACCOUNT" ||
      userInfo?.account_type === "NORMAL_SUB_ACCOUNT" ||
      userAccountType === "NORMAL_SUB_ACCOUNT"
    ) {
      nav = nav.filter((item) => item.isSupportSubAccount);
    }

    return nav;
  }, [assets, currentAccountMode, isDemoTrading, userInfo?.account_type, userAccountType]);

  useEffect(() => {
    initAccountMode();
    initNewUserTaskInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PopOver
      trigger="group-hover"
      anchor="top-right"
      onMouseEnter={() => setPopoverVisible(true)}
      onMouseLeave={() => setPopoverVisible(false)}
      containerClassName={styles["assets-container"]}
      content={
        <div className={stylesBg({ mode: theme })}>
          <div
            className={clsx(
              theme === "dark" ? styles["scroll-container-dark"] : styles["scroll-container"]
            )}
          >
            {showUnlockableRewardCard && showUnlockableReward && newUserTaskInfo ? (
              <UnlockableRewardCard taskInfo={newUserTaskInfo} visible={popoverVisible} />
            ) : (
              <AssetsCard
                visible={popoverVisible}
                accountType={userInfo?.account_type}
              ></AssetsCard>
            )}
            <div className="px-[12px] pb-[12px]">
              {menus.map((item, index) => {
                if (item?.type === "line")
                  return (
                    <div
                      key={index}
                      className={clsx(
                        "my-[16px] h-[1px] mx-[12px]",
                        popperDivider({ mode: theme })
                      )}
                    ></div>
                  );
                return (
                  <a
                    key={index}
                    href={`${localeLink}/${item.link}`}
                    className={clsx(
                      "h-[44px] px-[12px] flex items-center text-[14px] font-medium rounded-[8px]",
                      popperItemHover({ mode: theme }),
                      dropDownStylesText({ mode: theme })
                    )}
                  >
                    <item.icon className={styles["assets-icon"]} />
                    {t(item.label)}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      }
    >
      <div
        className={clsx(
          styles["assets-text"],
          stylesText({ mode: theme }),
          "h-full",
          popperTriggerHover({ mode: theme }),
          theme === "dark"
            ? "[&>svg>path]:hover:fill-dark-primary"
            : "[&>svg>path]:hover:fill-light-primary"
        )}
      >
        {t("assets")}
        <ArrowIcon
          className={`${styles["assets-arrow"]} ${stylesText({
            mode: theme
          })}`}
        />
      </div>
    </PopOver>
  );
};

export default AssetsPopper;
